import React, {
  ReactElement,
  useEffect,
  useRef,
  useState,
  useContext,
} from "react";
import classNames from "classnames";
import { MenuItemProps } from "./menu-item.types";
import { NavContext } from "./navContext";
import Badge from "../badge";
import { renderIconClassName } from "../utils";

export const MenuItem = ({
  children,
  className,
  groupLabel,
  id,
  link,
  onClick,
  title,
  labelAsCollapse,
  skipItemActivation,
  _privateProps = {},
  // isActive and defaultActive are not used here. They were previously calculated and included in reducer action. Stays here to be ignore on ...rest.
  isActive,
  defaultActive,
  ...rest
}: MenuItemProps): ReactElement => {
  const {
    isHeader,
    isHeaderSecondary,
    isMoreNavItem,
    isMoreNavItemActive,
    isTopLevelItem,
    closeOnClick,
    handleCloseOnClick,
    iconName,
    iconBadgeProps,
    spotlight,
  } = _privateProps;

  const [hasFocus, setHasFocus] = useState(false);
  const {
    state: {
      addActive,
      toggleExpand,
      activeItem,
      expandedItems,
      activeItemParents,
    },
  } = useContext(NavContext);

  const menuItemRef = useRef<HTMLLIElement>(null);
  const submenuRef = useRef<HTMLUListElement>(null);

  const isExpanded = id ? expandedItems.includes(id) : false;
  const hasActiveChild = id ? activeItemParents.includes(id) : false;

  useEffect(() => {
    if (isExpanded !== null) {
      const expand = (): Promise<void> => {
        return new Promise((resolve) => {
          const control = menuItemRef.current?.querySelector(
            ".spark-menu__list-expand"
          );
          const content: HTMLElement | undefined | null =
            menuItemRef.current?.querySelector("ul.spark-menu__list");

          if (control && content) {
            /**
             * Transition animation breaks some Header's features so
             * it's added only to standalone PanelMenu
             */
            if (!isHeader) {
              const height = Math.round(content.scrollHeight);

              content.style.height = height + "px";
              content.addEventListener("transitionend", function onExpand() {
                content.removeEventListener("transitionend", onExpand);
                content.style.height = "auto";
                resolve();
              });
            }
            control.setAttribute("aria-expanded", "true");
          }
        });
      };

      const collapse = (): void => {
        const control = menuItemRef.current?.querySelector(
          ".spark-menu__list-expand"
        );
        const content: HTMLElement | undefined | null =
          menuItemRef.current?.querySelector("ul.spark-menu__list");

        if (control && content) {
          control.setAttribute("aria-expanded", "false");
          if (!isHeader) {
            content.style.height = "0px";
          }
        }
      };

      isExpanded ? expand() : collapse();
    }
  }, [isExpanded, isHeader]);

  const spotlightLink = spotlight && !isTopLevelItem && !children;
  const isParentItemCollapseOnly = !!children && labelAsCollapse;

  const menuItemClasses = classNames(
    { "spark-menu__list-item": !groupLabel },
    { "spark-header__list-cta ": spotlightLink },
    // The 'open' class applies a dim effect that Spark doesn't apply to "More Nav"
    { open: !isMoreNavItem && isExpanded },
    { "has-focus": hasFocus },
    { "top-level-item": isHeader && (isTopLevelItem || isMoreNavItem) },
    { active: activeItem && activeItem === id },
    {
      "child-active": hasActiveChild || (isMoreNavItem && isMoreNavItemActive),
    },
    { "spark-menu__list-title": groupLabel },
    { "spark-header__more": isMoreNavItem },
    { "spark-no-animate": isMoreNavItem },
    className
  );

  const onClickHandler = (e: React.MouseEvent<HTMLElement>): void => {
    (!link || isParentItemCollapseOnly) && e.preventDefault();

    if (isHeader && closeOnClick && !isParentItemCollapseOnly) {
      setHasFocus(false);
      handleCloseOnClick?.(menuItemRef, isTopLevelItem);
    }

    if (!(isParentItemCollapseOnly || skipItemActivation)) {
      if (id) {
        addActive?.(id, isHeaderSecondary);
      }
    }

    onClick?.(e);
  };

  const onExpandHandler = (): void => {
    if (id) {
      toggleExpand?.(id, isHeaderSecondary);
    }
  };

  const renderItemAnchor = (
    title: string,
    anchorProps: typeof menuAnchorProps
  ): ReactElement => (
    <a {...anchorProps}>
      {iconName ? (
        <>
          <i aria-hidden="true" className={renderIconClassName(iconName)} />{" "}
        </>
      ) : null}
      {title}
    </a>
  );

  const _renderSublevelItems = (
    submenuItems?: React.ReactNode
  ): ReactElement | null => {
    return submenuItems ? (
      <ul
        className={classNames("spark-menu__list", {
          "spark-menu__list_transition": !isHeader,
        })}
        ref={submenuRef}
      >
        {React.Children.map(submenuItems, (child) => {
          if (!React.isValidElement(child)) {
            return null;
          }

          const childType: any = child.type;

          return childType.displayName === "MenuItem"
            ? React.cloneElement(child as ReactElement<any>, {
                _privateProps: {
                  ...child.props._privateProps,
                },
              })
            : null;
        })}
      </ul>
    ) : null;
  };

  const _renderSublevelParent = (
    anchorProps: typeof menuAnchorProps
  ): ReactElement => {
    /**
     * render submenus parent items
     */
    const renderParentAsLinkAndCollapse = (): ReactElement => (
      <>
        {renderItemAnchor(title, anchorProps)}
        <button
          type="button"
          className="spark-menu__list-expand"
          onClick={onExpandHandler}
          aria-label={`Expand or Collapse ${title} Menu`}
        ></button>
      </>
    );

    const renderParentAsCollapse = (): ReactElement => {
      return isTopLevelItem ? (
        <>
          <a
            {...anchorProps}
            aria-label={`Expand or Collapse ${title} Menu`}
            onClick={(e): void => {
              anchorProps.onClick?.(e);
              onExpandHandler();
            }}
          >
            {title}
          </a>
          <button
            type="button"
            className="spark-menu__list-expand"
            onClick={onExpandHandler}
            aria-label={`Expand or Collapse ${title} Menu`}
          ></button>
        </>
      ) : (
        <a
          {...anchorProps}
          onClick={(e): void => {
            anchorProps.onClick?.(e);
            onExpandHandler();
          }}
          aria-label={`Expand or Collapse ${title} Menu`}
        >
          {title}
        </a>
      );
    };

    return (
      <>
        <span className="spark-menu__list-links">
          {isParentItemCollapseOnly
            ? renderParentAsCollapse()
            : renderParentAsLinkAndCollapse()}
        </span>
        {_renderSublevelItems(children)}
      </>
    );
  };

  const _renderSecondarySublevelParent = (
    menuProps: typeof menuAnchorProps
  ): ReactElement => {
    const iconClasses = classNames(
      { [`${renderIconClassName(iconName)}`]: iconName },
      "spark-icon--fill",
      "spark-icon--md"
    );

    const linkElement = (
      <a
        {...menuProps}
        onClick={(e): void => {
          menuProps.onClick?.(e);
          if (isParentItemCollapseOnly) {
            onExpandHandler();
          }
        }}
        aria-label={
          iconBadgeProps?.ariaLabel && iconBadgeProps?.label
            ? `${title}, ${iconBadgeProps.ariaLabel}`
            : title
        }
      >
        {iconName ? <i className={iconClasses} /> : null}
        <span
          className={classNames({ "spark-header__collapsed-show": iconName })}
        >
          {title}
        </span>
        {iconBadgeProps?.label ? (
          <Badge notification {...iconBadgeProps} />
        ) : null}
      </a>
    );

    const expandButtonElement = (
      <button
        type="button"
        className="spark-menu__list-expand"
        onClick={onExpandHandler}
        aria-label={`Expand or Collapse ${title} Menu`}
      />
    );

    return children ? (
      <>
        <span className="spark-menu__list-links">
          {linkElement}
          {expandButtonElement}
        </span>
        {_renderSublevelItems(children)}
      </>
    ) : (
      linkElement
    );
  };

  const menuAnchorProps = {
    ...rest,
    id: id,
    className: classNames(
      { "spark-menu__list-link": !spotlightLink },
      {
        "spark-header__list-cta-link": spotlightLink,
      },
      {
        "spark-menu__list-expand": isParentItemCollapseOnly && !isTopLevelItem,
      },
      className
    ),
    href: link || "#",
    onClick: onClickHandler,
  };

  const renderMenuItem = (): ReactElement =>
    isHeaderSecondary && isTopLevelItem
      ? renderSecondaryMenuItem()
      : renderStandardMenuItem();

  const renderStandardMenuItem = (): ReactElement => (
    <li
      className={menuItemClasses}
      ref={menuItemRef}
      onFocus={(): void => setHasFocus(true)}
      onBlur={(): void => setHasFocus(false)}
    >
      {children
        ? _renderSublevelParent(menuAnchorProps)
        : groupLabel
        ? title
        : renderItemAnchor(title, menuAnchorProps)}
    </li>
  );

  const renderSecondaryMenuItem = (): ReactElement => (
    <li
      className={classNames(
        "spark-menu__list-item--secondary",
        menuItemClasses
      )}
      role="menuitem"
      ref={menuItemRef}
      onFocus={(): void => setHasFocus(true)}
      onBlur={(): void => setHasFocus(false)}
    >
      {_renderSecondarySublevelParent(menuAnchorProps)}
    </li>
  );

  const renderMoreNavItem = (): ReactElement => (
    <li
      className={menuItemClasses}
      ref={menuItemRef}
      id="more-nav-items"
      onFocus={(): void => setHasFocus(true)}
      onBlur={(): void => setHasFocus(false)}
    >
      <a className="spark-menu__list-link" tabIndex={0} aria-label={title}>
        <i className="spark-icon-menu-ellipsis-horizontal spark-icon--fill"></i>
      </a>
      {_renderSublevelItems(children)}
    </li>
  );

  return isMoreNavItem ? renderMoreNavItem() : renderMenuItem();
};

MenuItem.displayName = "MenuItem";
